import React from 'react';
import './FlightFilters.css';

/**
 * FlightFilters Component
 * 
 * Left sidebar with filters for:
 * - Stops (nonstop, 1 stop)
 * - Departure time
 * - Airlines
 */
const FlightFilters = ({ 
  flights = [],
  selectedStops = [],
  selectedAirlines = [],
  onStopsChange,
  onAirlinesChange
}) => {
  
  // Get unique airlines with prices
  const airlines = React.useMemo(() => {
    const airlineMap = new Map();
    flights.forEach(flight => {
      const code = flight.airlineCode || flight.carrierCode;
      const name = flight.airline;
      const price = flight.price;
      
      if (!airlineMap.has(code) || airlineMap.get(code).price > price) {
        airlineMap.set(code, { code, name, price });
      }
    });
    return Array.from(airlineMap.values()).sort((a, b) => a.price - b.price);
  }, [flights]);

  // Get stop counts with prices
  const stopOptions = React.useMemo(() => {
    const stopMap = new Map();
    flights.forEach(flight => {
      const stops = flight.stops || 0;
      if (!stopMap.has(stops) || stopMap.get(stops) > flight.price) {
        stopMap.set(stops, flight.price);
      }
    });
    
    return [
      { value: 0, label: 'Nonstop', price: stopMap.get(0) },
      { value: 1, label: '1 Stop', price: stopMap.get(1) }
    ].filter(opt => opt.price !== undefined);
  }, [flights]);

  const handleStopChange = (stopValue) => {
    const newStops = selectedStops.includes(stopValue)
      ? selectedStops.filter(s => s !== stopValue)
      : [...selectedStops, stopValue];
    onStopsChange(newStops);
  };

  const handleAirlineChange = (airlineCode) => {
    const newAirlines = selectedAirlines.includes(airlineCode)
      ? selectedAirlines.filter(a => a !== airlineCode)
      : [...selectedAirlines, airlineCode];
    onAirlinesChange(newAirlines);
  };

  if (flights.length === 0) return null;

  return (
    <div className="flight-filters">
      <h3 className="filters-title">Filter Results</h3>

      {/* Stops Filter */}
      <div className="filter-section">
        <h4 className="filter-heading">STOPS</h4>
        <div className="filter-options">
          {stopOptions.map(option => (
            <label key={option.value} className="filter-checkbox">
              <input
                type="checkbox"
                checked={selectedStops.includes(option.value)}
                onChange={() => handleStopChange(option.value)}
              />
              <span className="filter-label">
                {option.label}
              </span>
              <span className="filter-price">${option.price}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Airlines Filter */}
      <div className="filter-section">
        <h4 className="filter-heading">AIRLINES</h4>
        <div className="filter-options">
          {airlines.map(airline => (
            <label key={airline.code} className="filter-checkbox">
              <input
                type="checkbox"
                checked={selectedAirlines.includes(airline.code)}
                onChange={() => handleAirlineChange(airline.code)}
              />
              <span className="filter-label">
                {airline.name}
              </span>
              <span className="filter-price">${airline.price}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlightFilters;